// Protected Route Component - Requires authentication

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDefaultLandingPage, getNonAdminRedirect, hasAdminAccess } from '../../utils/landingPage';
import { NotAuthorizedPage } from '../../pages/NotAuthorizedPage';
import type { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredScope?: string;
  /**
   * Admin-panel routes (/dashboard and the other MainLayout pages whose API
   * is admin-only). Admits ADMIN-role users and anyone holding an explicit
   * admin:* grant (utils/landingPage hasAdminAccess); everyone else is sent to
   * their own landing page — the portal door they normally log in to — or, if
   * that landing IS the admin panel, shown a "not authorized" page rather than
   * the admin shell firing 403s on every data call.
   */
  adminOnly?: boolean;
}

export function ProtectedRoute({ children, requiredRole, requiredScope, adminOnly }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, hasScope } = useAuth();
  const location = useLocation();

  // Show nothing while checking auth state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #e5e7eb',
          borderTopColor: '#0B4FA3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the target URL
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} state={{ from: location }} replace />;
  }

  // Determine the appropriate fallback page for permission denials. A denial
  // whose fallback is the route we are already on would loop, so that case
  // renders the not-authorized page in place instead of navigating.
  const fallback = user ? getDefaultLandingPage(user) : '/dashboard';
  const deny = (destination: string | null) =>
    destination === null || destination === location.pathname
      ? <NotAuthorizedPage />
      : <Navigate to={destination} replace />;

  // Admin-only routes: role ADMIN or an explicit admin:* grant.
  if (adminOnly && user && !hasAdminAccess(user)) {
    return deny(getNonAdminRedirect(user));
  }

  // Check role if required
  if (requiredRole && user) {
    const roleHierarchy: Record<UserRole, number> = {
      ADMIN: 4,
      MANAGER: 3,
      USER: 2,
      READONLY: 1,
      SALESPERSON: 1,
    };

    if (roleHierarchy[user.role] < roleHierarchy[requiredRole]) {
      return deny(fallback);
    }
  }

  // Check scope if required
  if (requiredScope && !hasScope(requiredScope)) {
    return deny(fallback);
  }

  return <>{children}</>;
}
